import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { colors } from '~/styles';
import { useAssistant } from './AssistantContext';
import AssistantMessage from './AssistantMessage';
import Suggestions from './Suggestions';
import usePolling from './usePolling';
import useStream from './useStream';
import WelcomeState from './WelcomeState';

const S = {
  ChatArea: styled.div`
    display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
    flex: 1;
    height: 100%;
    overflow: hidden;
    flex-direction: column;
    font-size: 0.875rem;
  `,

  ChatScroll: styled.div`
    flex: 1;
    overflow-y: auto;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar {
      width: 0.875rem;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.grey25};
      border-radius: 2.5rem;
      border: 0.25rem solid transparent;
      background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.grey55};
      background-clip: padding-box;
    }
  `,

  Chat: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: ${({ $constrained }) => ($constrained ? '820px' : 'none')};
    margin: 0 auto;
    min-height: 100%;
  `,

  Messages: styled.div`
    flex: 1;
    padding: 1rem 1rem 1rem 1.875rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  `,

  UserMessage: styled.div`
    background: ${colors.primary10};
    color: ${colors.black};
    padding: 1.5rem 1.25rem;
    border-radius: 0.625rem 0.625rem 0 0.625rem;
    white-space: pre-wrap;
    line-height: 1.5;
    opacity: ${({ $failed }) => ($failed ? 0.6 : 1)};
  `,

  UserMessageWrapper: styled.div`
    max-width: 80%;
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  `,

  FailedIndicator: styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.375rem;
    font-size: 0.75rem;
    color: ${colors.danger};
  `,

  RetryButton: styled.button`
    background: none;
    border: none;
    border-radius: 0;
    padding: 0;
    color: ${colors.danger};
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 400;
    text-decoration: underline;
    height: auto;

    &:hover {
      color: ${colors.danger100};
      background: none;
    }
  `,

  FooterWrapper: styled.div`
    background: ${colors.white};
    padding: 0.5rem 1.875rem 1.0625rem;
    flex-shrink: 0;
    width: 100%;
    max-width: ${({ $constrained }) => ($constrained ? '820px' : 'none')};
    margin: 0 auto;
  `,

  FooterInput: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,

  PlusButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    min-height: 2rem;
    padding: 0;
    border: 1px solid ${colors.grey10};
    border-radius: 50%;
    background: transparent;
    color: ${colors.grey40};
    cursor: pointer;
    flex-shrink: 0;
    font-size: 0.75rem;
    box-sizing: border-box;

    &:hover {
      background: ${colors.grey5};
      color: ${colors.grey55};
    }
  `,

  TextareaWrapper: styled.div`
    flex: 1;
    position: relative;
    display: flex;
  `,

  Textarea: styled.textarea`
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${colors.grey10};
    border-radius: 0.625rem;
    padding: 0.8125rem 2.5rem 0.625rem 0.8125rem;
    outline: none;
    resize: none;
    font-size: 0.875rem;

    &::placeholder {
      font-style: italic;
      color: ${colors.grey40};
    }

    &:focus {
      border-color: ${colors.grey10};
      outline: none;
    }
  `,

  ConnectionWarning: styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    color: ${colors.warning100};
    background: ${colors.warning10};
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
  `,

  SendButton: styled.button`
    position: absolute;
    right: 0.625rem;
    bottom: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.875rem;
    height: 1.875rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: ${colors.grey5};
    color: ${colors.primary};
    cursor: pointer;
    font-size: 0.6875rem;
    box-sizing: border-box;

    &:hover {
      background: ${colors.grey5};
      color: ${colors.accent};
    }

    &:disabled {
      background: ${colors.grey5};
      color: ${colors.grey25};
      opacity: 1;
      cursor: default;
    }
  `,

  Disclaimer: styled.p`
    font-size: 0.625rem;
    color: ${colors.grey25};
    text-align: center;
    margin-top: 0.8125rem;
  `,
};

export default function Assistant({
  model,
  conversationId,
  onConversationIdChange,
  onConversationsChange,
  onThinkingChange,
  hidden,
}) {
  const api = useApi();
  const toast = useToast();
  const { workspace } = useWorkspace();
  const { profileId, setProfileId, mode, closePanel } = useAssistant();
  const isMounted = useIsMounted();

  const [messages, setMessages] = useState(null);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('ok'); // 'ok' | 'slow' | 'dead'

  const stream = useStream({ setMessages, setThinking });

  const refreshConversations = useCallback(() => {
    api.www
      .workspaces(workspace.id)
      .ai()
      .conversations()
      .list()
      .then(({ data }) => onConversationsChange({ data, isReady: true, error: null }))
      .catch(() => {});
  }, [api, workspace.id, onConversationsChange]);

  const { start: startPolling, stop: stopPolling } = usePolling({
    setMessages,
    setThinking,
    onComplete: refreshConversations,
  });

  const textareaRef = useRef(null);
  const prevConversationIdRef = useRef(undefined);

  const loadConversation = useCallback(
    async (id) => {
      stream.abort();
      stopPolling();
      setThinking(false);
      setConnectionStatus('ok');

      if (!id) {
        setMessages(null);
        textareaRef.current?.focus();
        return;
      }

      try {
        const { data } = await api.www.workspaces(workspace.id).ai().conversations(id).get();
        if (!isMounted.current) return;
        if (data.assistantProfileId) {
          setProfileId(data.assistantProfileId);
        }

        // Still streaming — show thinking and poll for completion
        if (data.responseStatus === 'streaming') {
          const msgs = [...data.messages];
          if (!msgs.length || msgs[msgs.length - 1].role === 'user') {
            msgs.push({ role: 'assistant', parts: [] });
          }
          setMessages(msgs);
          setThinking(true);
          startPolling(id);
          return;
        }

        // Idle — normal load
        setMessages(data.messages);
        textareaRef.current?.focus();
      } catch {
        if (!isMounted.current) return;
        toast.error('Could not load this conversation. Please try again.');
      }
    },
    [api, workspace.id, isMounted, setProfileId, stream, toast, startPolling, stopPolling],
  );

  // Load conversation when conversationId changes (sidebar selection, new chat)
  useEffect(() => {
    if (conversationId === prevConversationIdRef.current) return;
    prevConversationIdRef.current = conversationId;
    loadConversation(conversationId);
  }, [conversationId, loadConversation]);

  // Sync thinking state to parent
  useEffect(() => {
    onThinkingChange?.(thinking);
  }, [thinking, onThinkingChange]);

  const handleSend = async (text) => {
    if (thinking) return;
    const userText = text || input.trim();
    if (!userText) return;

    const next = [
      ...(messages ?? []).filter((m) => !m._failed),
      { role: 'user', parts: [{ type: 'input_text', text: userText }] },
    ];
    next.push({ role: 'assistant', parts: [] });

    setMessages(next);
    setInput('');

    try {
      const baseUrl = `/api/www/workspaces/${workspace.id}/ai/conversations`;
      const payload = { model, text: userText, assistantProfileId: profileId };
      if (!payload.model) delete payload.model;

      const connectionCallbacks = {
        onConnectionSlow: () => setConnectionStatus('slow'),
        onConnectionRecovered: () => setConnectionStatus('ok'),
        onConnectionDead: () => setConnectionStatus('dead'),
      };

      if (conversationId) {
        await stream.run(`${baseUrl}/${conversationId}/messages`, payload, connectionCallbacks);
      } else {
        await stream.run(baseUrl, payload, {
          ...connectionCallbacks,
          onConversationCreated: (id) => {
            prevConversationIdRef.current = id;
            onConversationIdChange(id);
            refreshConversations();
          },
          afterComplete: async () => {
            refreshConversations();

            try {
              const id = prevConversationIdRef.current;
              if (!id) return;
              const { data } = await api.www.workspaces(workspace.id).ai().conversations(id).get();
              if (prevConversationIdRef.current !== id) return;
              setMessages(data.messages);
            } catch {
              if (!isMounted.current) return;
              toast.warning('Could not refresh conversation. Please reload if the response looks incomplete.');
            }
          },
        });
      }

      setConnectionStatus('ok');
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (!isMounted.current) return;

      const errorMessage = error.message || 'Sorry, something went wrong. Please try again.';

      if (error.code === 'AI_BUDGET_LIMIT') {
        // Non-recoverable: maintain User → Assistant pattern with error part
        setMessages((msgs) => {
          if (!msgs) return msgs;
          const updated = [...msgs];
          const last = updated[updated.length - 1];
          if (last?.role === 'assistant') {
            last.parts.push({ type: 'error', message: errorMessage, code: error.code });
          }
          return updated;
        });
      } else {
        // Recoverable: mark user message as failed with retry affordance
        setMessages((msgs) => {
          if (!msgs) return msgs;
          const updated = [...msgs];
          // Remove trailing empty assistant message
          if (updated[updated.length - 1]?.role === 'assistant' && updated[updated.length - 1].parts.length === 0) {
            updated.pop();
          }
          // Mark last user message as failed
          const lastUser = updated[updated.length - 1];
          if (lastUser?.role === 'user') {
            lastUser._failed = true;
            lastUser._error = errorMessage;
          }
          return updated;
        });
      }
      setThinking(false);
    }
  };

  const lastUserIndex = React.useMemo(() => {
    if (!messages?.length) return -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return i;
    }
    return -1;
  }, [messages]);

  const lastUserAnchorRef = React.useRef(null);

  useEffect(() => {
    lastUserAnchorRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }, [lastUserIndex]);

  const handleRefreshConversation = useCallback(() => {
    if (!conversationId) return;
    loadConversation(conversationId);
  }, [conversationId, loadConversation]);

  const handleChatClick = useCallback(
    (e) => {
      if (mode !== 'drawer') return;
      const anchor = e.target.closest('a');
      if (anchor && !anchor.target) closePanel();
    },
    [mode, closePanel],
  );

  const isConstrained = true;
  const showWelcome = !messages || messages.length === 0;
  const showSuggestions = !thinking && !conversationId;

  return (
    <S.ChatArea $hidden={hidden}>
      <S.ChatScroll onClick={handleChatClick}>
        <S.Chat $constrained={isConstrained}>
          {showWelcome ? (
            <WelcomeState />
          ) : (
            <S.Messages key={conversationId}>
              {messages?.map((msg, i) => {
                const isLastUser = i === lastUserIndex;
                const isLast = i === messages.length - 1;

                return (
                  <React.Fragment key={i}>
                    {msg.role === 'user' && (
                      <>
                        {isLastUser && <div ref={lastUserAnchorRef} style={{ scrollMarginTop: '1rem' }} />}
                        <S.UserMessageWrapper>
                          <S.UserMessage $failed={!!msg._failed}>
                            {msg.parts.map((part, j) => (
                              <React.Fragment key={j}>{part.text}</React.Fragment>
                            ))}
                          </S.UserMessage>
                          {msg._failed && (
                            <S.FailedIndicator>
                              <Icon icon="exclamation-triangle" />
                              Failed to send
                              <S.RetryButton onClick={() => handleSend(msg.parts[0].text)}>Retry</S.RetryButton>
                            </S.FailedIndicator>
                          )}
                        </S.UserMessageWrapper>
                      </>
                    )}

                    {msg.role === 'assistant' && (
                      <AssistantMessage
                        message={msg}
                        conversationId={conversationId}
                        isLast={isLast}
                        thinking={thinking && isLast}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </S.Messages>
          )}
        </S.Chat>
      </S.ChatScroll>

      <S.FooterWrapper $constrained={isConstrained}>
        {connectionStatus === 'slow' && (
          <S.ConnectionWarning>
            <Icon icon="exclamation-triangle" />
            Connection is slow. Waiting for response...
          </S.ConnectionWarning>
        )}
        {connectionStatus === 'dead' && (
          <S.ConnectionWarning>
            <Icon icon="exclamation-triangle" />
            Connection may be lost.
            <S.RetryButton onClick={handleRefreshConversation}>Refresh</S.RetryButton>
          </S.ConnectionWarning>
        )}
        {showSuggestions && <Suggestions conversationId={conversationId} onSend={handleSend} />}
        <S.FooterInput>
          <S.TextareaWrapper>
            <S.Textarea
              autoFocus
              rows={4}
              placeholder="What can I help you with?"
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!thinking) handleSend();
                }
              }}
            />
            <S.SendButton onClick={() => handleSend()} disabled={thinking || !input.trim()} title="Send">
              <Icon icon="paper-plane-top" />
            </S.SendButton>
          </S.TextareaWrapper>
        </S.FooterInput>
        <S.Disclaimer>
          Ruddr AI is in beta and may make mistakes.
          <br />
          Data and analysis provided are based on your workspace permissions.
        </S.Disclaimer>
      </S.FooterWrapper>
    </S.ChatArea>
  );
}
